import { keyframes, styled } from "goober";
import { playClick } from "../lib/sounds";
import { Icon } from "./Icon";
import { isInAppNavigation, Link } from "./Link";

const nudge = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-2px);
  }
`;

// Takes the Home and Chat switch’s place in the header.
export const BackPill = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 32px;
  padding: 8px 6px 8px 12px;
  border-radius: 999px;
  background: var(--alpha4);
  color: inherit;
  text-decoration: none;
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 2px;
  }
  & [data-back-icon] {
    display: grid;
    flex-shrink: 0;
  }
  & [data-back-icon] > span {
    display: flex;
    grid-area: 1 / 1;
  }
  & [data-back-badge],
  & [data-back-arrow] {
    transition: opacity var(--motion-fast) var(--ease-out);
  }
  & [data-back-badge] + [data-back-arrow] {
    opacity: 0;
  }
  &:hover,
  &:focus-visible {
    background: var(--alpha8);
  }
  &:hover [data-back-arrow],
  &:focus-visible [data-back-arrow] {
    opacity: 1;
    animation: ${nudge} 700ms ease-in-out infinite alternate;
  }
  &:hover [data-back-badge],
  &:focus-visible [data-back-badge] {
    opacity: 0;
  }
`;

export const BackPillChip = styled("span")`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px 0 6px;
  height: 20px;
  background: var(--alpha4);
  border-radius: 38px;
  color: var(--text-secondary);
  min-width: 0;
  max-width: 50%;
  margin-left: auto;
  flex-shrink: 0;
`;

// Shows `children`, if any, until the pill is hovered, then a back arrow.
export function BackPillIcon({ children }: { children?: React.ReactNode }) {
  return (
    <span data-back-icon="" aria-hidden="true">
      {children && <span data-back-badge="">{children}</span>}
      <span data-back-arrow="">
        <Icon name="arrow-left" size={16} />
      </span>
    </span>
  );
}

// Steps back to the nearest earlier history entry `skip` rejects instead of
// pushing the link, so two back pills can’t bounce between each other’s pages.
export function traverseBackPast(
  event: React.MouseEvent<HTMLAnchorElement>,
  skip: (url: URL) => boolean
) {
  if (!isInAppNavigation(event)) return;
  const { navigation } = window as Window & {
    navigation?: {
      currentEntry: NavigationHistoryEntry;
      entries(): NavigationHistoryEntry[];
      traverseTo(key: string): { committed?: Promise<unknown> };
    };
  };
  const entry = navigation
    ?.entries()
    .slice(0, navigation.currentEntry.index)
    .findLast(({ url }) => url && !skip(new URL(url)));
  if (entry) {
    event.preventDefault();
    playClick();
    navigation!.traverseTo(entry.key).committed?.catch(() => {
      // A restored tab’s history can’t be traversed.
      // https://tines.sentry.io/issues/7809172564/
    });
  }
}
